/* Copyright (c) 2023 MOCA Systems, Inc.  All rights reserved. */

import * as appStrings from '@touchplan/strings';
import { Injectable, inject } from '@angular/core';
import {
	Auth,
	signOut,
	signInWithEmailAndPassword,
	signInWithRedirect,
	signInWithPopup,
	SAMLAuthProvider,
	AuthProvider,
	sendPasswordResetEmail,
	createUserWithEmailAndPassword,
	EmailAuthProvider,
	reauthenticateWithCredential,
	reauthenticateWithRedirect,
	getRedirectResult,
	getIdToken,
	getAuth,
	OAuthProvider,
	authState,
	reauthenticateWithPopup,
} from '@angular/fire/auth';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { analyticsEventsService } from '../plain-javascript/services/analytics/AnalyticsEventsService';
import { analyticsService } from 'analytics';

import {
	fancyFirebaseGetters,
	fancyFirebaseReferencer,
	DEFAULT_TENANT_ID,
} from '@touchplan/fancy-referencer';
import { Observable, filter, from, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import {
	IDomainTenant,
	IEmailTenantIndex,
	Tenant,
	IEmailTenantExclusive,
	escapeEmail,
} from '@touchplan/common';
import { BannerMessageHandler } from '../common/logging/banner-message-handler';
import { tenantGetterService } from '@touchplan/firebase-common';
import { getApps } from '@angular/fire/app';
import { browserLocalPersistence, setPersistence } from 'firebase/auth';
import { fancyDatabase } from '@touchplan/client-firebase';

import firebase from 'firebase/compat/app';

/** temp (potentially "forever temp" version that assumes there's a single providerId) */
export interface IEmailTenantExclusiveWithLocalData extends IEmailTenantExclusive {
	tenant: Tenant;
	email: string; //store the email key as a property
}

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private auth: Auth = inject(Auth);
	private angularFireDatabase: AngularFireDatabase = inject(AngularFireDatabase);
	private bannerMessageHandler: BannerMessageHandler = inject(BannerMessageHandler);

	set tenant(tenant: Tenant) {
		if (!tenant || !tenant.authTenantId || tenant.authTenantId === DEFAULT_TENANT_ID) {
			this.auth.tenantId = null;
		}
		this.auth.tenantId = tenant.authTenantId;
	}

	/**
	 * the providerId associated with user. Will be null if not authenticated
	 * relies on the idea that we don't currently support more than one provider being authed at once
	 * (no multi-factor)
	 * */
	public get currentProviderId() {
		return this.auth.currentUser?.providerData[0]?.providerId;
	}

	constructor() {
		console.log('auth', this.auth);
		// Some temp test code added for https://mocasystems.atlassian.net/browse/TP-6229
		window.authServiceTest = this;
		window.authServiceTestSSO = () => {
			this.reauthenticateWithSSO(this.auth.currentUser.providerId)
				.then(() => {
					console.log('reauthenticated');
				})
				.catch((e) => {
					console.log('reauthenticateWithSSO error', e);
				});
		};
	}

	/** "safely" triggers an auth token refresh immediately */
	public forceTokenRefresh() {
		const currentUser = this.auth.currentUser;
		if (!currentUser) {
			return null;
		}
		return getIdToken(currentUser, true);
	}

	public forceSessionPersistence() {
		setPersistence(this.auth, browserLocalPersistence).catch((e) => console.log('setPersistence error', e));
	}

	public async signInWithEmailAndPassword(email: string, password: string, authOverride?: Auth) {
		const auth = authOverride || this.auth;
		await setPersistence(auth, browserLocalPersistence);
		return await signInWithEmailAndPassword(auth, email, password);
	}

	public createUserWithEmailAndPassword(email: string, password: string) {
		return createUserWithEmailAndPassword(this.auth, email, password);
	}

	public sendPasswordResetEmail(email: string) {
		return sendPasswordResetEmail(this.auth, email);
	}

	public getRedirectResult() {
		return getRedirectResult(this.auth);
	}

	/**
	 * Collect a user's password again to update their last login time. Necessary for cases
	 * where we want to be sure the user is the user. Like before they change their password, etc
	 */
	public reauthenticateWithEmailAndPassword(password: string, auth?: Auth) {
		if (!auth) {
			auth = this.auth;
		}
		const user = auth.currentUser;

		if (!user) {
			return Promise.reject(new Error('user must be authenticated'));
		}

		const credential = EmailAuthProvider.credential(user.email, password);
		return reauthenticateWithCredential(user, credential);
	}

	private preReauthSetup(providerId: string, auth?: Auth) {
		if (!auth) {
			auth = this.auth;
		}
		const user = auth.currentUser;

		if (!user) {
			return {};
		}

		//firebase has randomly decided to not respect currentUser.tenantId for reauth methods.
		//so clone it into the auth.........
		if (user.tenantId && auth.tenantId !== user.tenantId) {
			auth.tenantId = user.tenantId;
		}

		return { user, provider: this.createProviderInstance(providerId) };
	}

	/** thin wrapper that uses current auth and user */
	public reauthenticateWithRedirect(providerId: string, auth?: Auth) {
		const { user, provider } = this.preReauthSetup(providerId, auth);
		if (!user) {
			return Promise.reject(new Error('user must be authenticated'));
		}

		return reauthenticateWithRedirect(user, provider);
	}

	/** thin wrapper that uses current auth and user */
	public reauthenticateWithPopup(providerId: string, auth?: Auth) {
		const { user, provider } = this.preReauthSetup(providerId, auth);
		if (!user) {
			return Promise.reject(new Error('user must be authenticated'));
		}

		return reauthenticateWithPopup(user, provider);
	}

	/** reauth challenge that repects the enable popup override. Mostly for consistency */
	public reauthenticateWithSSO(providerId: string, auth?: Auth) {
		if (window?.enablePopupLogin) {
			return this.reauthenticateWithPopup(providerId, auth);
		}

		return this.reauthenticateWithRedirect(providerId, auth);
	}

	public logout() {
		//if necessary the analytics dependency hierarchy can be reversed by setting up a blocking hook
		analyticsEventsService.logOut();
		analyticsService.reset();
		const fbApps = getApps();
		if (fbApps.length) {
			const signOutPromises: Promise<void>[] = [];
			fbApps.forEach((app) => {
				const auth = getAuth(app);
				//don't check for logout, makes it not work when run during loading
				signOutPromises.push(signOut(auth));
			});
			if (signOutPromises.length) {
				Promise.all(signOutPromises).then(() => window.location.reload());
			}
		}
	}

	/**
	 * MVP for TP-5456, the idea is to make this available when absolutely necessary
	 * then make it easier to turn if a need is presented.
	 * For now, signInWithPopup can be enabled by putting
	 * `enablePopupLogin = true` into the dev-tools console
	 */
	private signInWithPopup(providerId: string, authOverride?: Auth) {
		const auth = authOverride || this.auth;
		return setPersistence(auth, browserLocalPersistence)
			.then(() => {
				return signInWithPopup(auth, this.createProviderInstance(providerId));
			})
			.then((cred) => {
				//half-ass solution to make it behave like the redirect
				window.location.reload();
			})
			.catch((e) => {
				console.log('signInWithPopup error', e);
				throw e;
			});
	}

	public signInWithRedirect(providerId: string, authOverride?: Auth) {
		const auth = authOverride || this.auth;

		if (window?.enablePopupLogin) {
			return this.signInWithPopup(providerId, authOverride);
		}

		return setPersistence(auth, browserLocalPersistence).then(() => {
			return signInWithRedirect(auth, this.createProviderInstance(providerId));
		});
	}

	// NOTE - this method relies on the idea that the user can only be part of a single tenant
	// and will thus need to be changed when we support more than one
	public queryTenantViaEmail(email: string): Observable<IEmailTenantExclusiveWithLocalData | null> {
		const encodedEmail = escapeEmail(email);
		const ref = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			fancyFirebaseGetters.getIndicesEmailTenantsPath(encodedEmail)
		); //.limitToFirst(1);

		console.log('querying email for tenantId at ' + ref.toString());

		return this.angularFireDatabase
			.object<IEmailTenantIndex>(ref)
			.valueChanges()
			.pipe(
				take(1),
				switchMap((val) => {
					if (!val) {
						return of(null);
					}

					//decorate local data
					const [rtdbTenantId, emailTenantMetadata] = Object.entries(val.exclusive)[0];

					return from(tenantGetterService.getTenantViaRtdbId(fancyDatabase, rtdbTenantId)).pipe(
						map((tenant) => {
							return { ...emailTenantMetadata, tenant, email };
						})
					);
				}),
				tap((tenantMeta) => {
					if (!tenantMeta) {
						console.log('tenantMeta not found');
					}
				})
			);
	}

	/**
	 * This method returns the tenant id for a given domain.
	 * @param domainKey Firebase encoded domain key
	 * @returns tenant id
	 */
	public queryTenantViaDomainKey(domainKey: string): Observable<Tenant> {
		if (!domainKey || domainKey === '') {
			return of(null);
		}
		const domainTenantRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			fancyFirebaseGetters.getDomainTenantsPath(domainKey)
		);
		console.log('querying domain tenant at ' + domainTenantRef.toString());
		return this.angularFireDatabase
			.object<IDomainTenant>(domainTenantRef)
			.valueChanges()
			.pipe(
				take(1),
				switchMap((domainTenant) => {
					if (domainTenant?.tenantId) {
						return from(tenantGetterService.getTenantViaRtdbId(fancyDatabase, domainTenant.tenantId));
					} else {
						return of(null as Tenant);
					}
				})
			);
	}

	public processRedirectResult() {
		from(getRedirectResult(this.auth)).subscribe(
			(result) => {
				const currentUser = result?.user;
				if (currentUser) {
					//BEGIN PLACEHOLDER REPLACE AS PART OF TP-5252
					// - most likely this is a bug in the library and a version bump should resolve this
					// - see also TP-6258 for context
					setPersistence(this.auth, browserLocalPersistence);

					setTimeout(() => {
						console.log('setting persistence to session, again');
						setPersistence(this.auth, browserLocalPersistence);
					}, 5000);
					//END PLACEHOLDER REPLACE AS PART OF TP-5252

					analyticsEventsService.login(currentUser.uid);
				}
			},
			(error) => {
				this.bannerMessageHandler.displayErrorMessage(
					error,
					appStrings.BANNER_MESSAGE_DEFAULT_LOGIN_ERROR_MESSAGE
				);
			}
		);
	}

	/**
	 * This method mirrors the lifecycle of "authState" (emit once on sign in, once on sign out).
	 * But only emits true if the user is an sso user
	 */
	public isUserSSO(): Observable<boolean> {
		return authState(this.auth).pipe(
			filter((userExists) => !!userExists),
			switchMap((firebaseUser) => {
				if (!firebaseUser) {
					return of(false);
				}

				return from(tenantGetterService.getTenantViaAuthId(fancyDatabase, firebaseUser.tenantId)).pipe(
					// If no tenant or tenant doesn't include metadata or tenant doesn't include providers assume it is the default tenant.
					map((tenant) =>
						!tenant || !tenant.tenantMetadata || !tenant.tenantMetadata.providers
							? Tenant.getDefaultTenant()
							: tenant
					),
					map((tenant) => !Object.keys(tenant.tenantMetadata.providers).includes('password'))
				);
			})
		);
	}

	private createProviderInstance(providerId: string): AuthProvider {
		if (!providerId) {
			return null;
		}
		const provider = providerId.split('.')[0];
		if (provider === 'saml') {
			return new SAMLAuthProvider(providerId);
		} else if (provider === 'oidc') {
			return new OAuthProvider(providerId);
		}
		return null;
	}
}
